import React, { useState, useEffect } from 'react';
import { Container } from 'reactstrap';
import CourseNewCard from '../Card/CourseNewCard';
import Amplify, { Auth } from 'aws-amplify';
import config from '../../aws-exports';
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/loader.json';
import { useLocation } from 'react-router';
import { cx, css } from '@emotion/css';
import * as assignmentStyles from '../assignments/assignments.style';
import spellBowlImg from '../../assets/gen_images/batchesicon.png';
import mathsImg from '../../assets/gen_images/mathematics.png';
import generalMathImg from '../../assets/gen_images/general_math.png';
import { withAuthenticator } from 'aws-amplify-react';

Amplify.configure(config);

const pageBgStyles = css`
  background-color: #fdfbf7;
  background-image: 
    radial-gradient(at 100% 0%, rgba(241, 102, 2, 0.04) 0px, transparent 50%),
    radial-gradient(at 0% 100%, rgba(244, 203, 14, 0.02) 0px, transparent 50%);
  min-height: 100vh;
  padding-bottom: 100px;
  overflow-x: hidden;
`;

const headerStyles = {
  container: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1rem;
    margin-bottom: 1.5rem;
    margin-top: 1.5rem;
    border-bottom: 1px solid rgba(241, 102, 2, 0.08);
    padding-bottom: 1.2rem;

    @media (max-width: 991px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1.2rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1.2rem;
    }
  `,
  leftCol: css`
    flex: 1;
    max-width: 850px;
  `,
  topLabel: css`
    font-family: 'Inter', sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: rgba(241, 102, 2, 0.7);
    margin-bottom: 0.6rem;
    display: block;
  `,
  heading: css`
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 2.4rem;
    font-weight: 800;
    font-style: italic;
    line-height: 1.05;
    margin: 0 0 0.5rem 0;
    letter-spacing: -0.02em;
    text-transform: uppercase;

    .orange-part {
      color: #f16602;
      margin-right: 0.6rem;
      display: inline-block;
    }

    .outline-part {
      color: transparent;
      -webkit-text-stroke: 1.5px rgba(241, 102, 2, 0.25);
      font-weight: 900;
      display: inline-block;
    }

    @media (max-width: 768px) {
      font-size: 1.8rem;
      
      .outline-part {
        -webkit-text-stroke: 1px rgba(241, 102, 2, 0.25);
      }
    }
  `,
  description: css`
    font-family: 'Inter', sans-serif;
    font-size: 1.05rem;
    color: #554a42;
    line-height: 1.6;
    margin: 0;
    max-width: 700px;
    font-weight: 400;

    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  `
};

function CoursesScreen(props) {
  const [events, set_events] = useState([]);
  const [loadedevents, set_loadedevents] = useState(false);
  const data1 = useLocation();

  useEffect(() => {
    getToken();
    document.title = 'Courses | QuizWizard';
  }, []);

  const getToken = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const res = await Auth.currentSession();
      const jwt = res.getAccessToken().getJwtToken();
      const username = user.username.includes("Google_") ? user.attributes['email'] : user.username;
      getEventsfromDB(username, jwt);
    } catch (error) {
      console.error("Auth error:", error);
      Auth.signOut();
    }
  };

  const getEventsfromDB = async (username, jwt) => {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: jwt },
      body: JSON.stringify({
        username: username,
        category: "Mathematics",
        eventtype: 'Curriculum',
      }),
    };
    
    try {
      const response = await fetch(`https://api.quizwizard.net/quiz/getcurriculumevents`, requestOptions);
      const data = await response.json();
      
      let loopData = [];
      data.forEach(item => {
        if (item.events) {
          loopData.push(...item.events);
        }
      });

      set_events(loopData);
      set_loadedevents(true);
    } catch (error) {
      console.error("Error fetching courses:", error);
      set_loadedevents(true);
    }
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
  };

  return (
    <div className={cx(pageBgStyles)}>
      <Container style={{ marginTop: '2.5rem' }}>
        {/* Curated Header Layout for Courses Page */}
        <div className={cx(headerStyles.container)}>
          <div className={cx(headerStyles.leftCol)}>
            <span className={cx(headerStyles.topLabel)}>AVAILABLE SUBJECTS</span>
            <h1 className={cx(headerStyles.heading)}>
              <span className="orange-part">Academic</span>
              <span className="outline-part">Curriculums</span>
            </h1>
            <p className={cx(headerStyles.description)}>
              Browse modular questions, select your academic track level, and launch interactive course practices.
            </p>
          </div>
        </div>

        {loadedevents ? (
          <div className={cx(assignmentStyles.challengeGrid)} style={{ padding: '0 0 80px 0' }}>
            {events.map((event) => (
              <CourseNewCard
                key={event.id}
                title={event.name}
                imgsrc={
                  event.category.toLowerCase().includes('spellbowl') ? spellBowlImg :
                  event.category.toLowerCase().includes('mathbowl') ? mathsImg :
                  event.category.toLowerCase().includes('math') ? generalMathImg :
                  'https://api.quizwizard.net/assets/' + event.category + '.png'
                }
                levels={event.levels}
                totalquestions={event.totalquestions}
                event={event}
                category={data1.state}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-5">
            <Lottie options={defaultOptions} height={200} width={200} />
            <p className="mt-3">Loading courses...</p>
          </div>
        )}
      </Container>
    </div>
  );
}

export default withAuthenticator(CoursesScreen, false);
